import React, { useCallback, useRef, useState } from 'react';
import { FileApi } from '../../../../services/api/FileApi';

/**
 * Step 1.5 — optional document attachments.
 *
 * Lets the user attach files (sports schedules, syllabi, project briefs,
 * activity calendars, etc.) that should inform the generated plan. Uploads
 * land in the user's document storage immediately via the existing
 * {@code POST /api/storage/upload} endpoint; the resulting document IDs are
 * passed to the AI-Service plan endpoints so the planner can extract
 * events / deadlines / blackouts / topics and treat them as constraints.
 *
 * Skipping this step is fully supported — the wizard then behaves exactly
 * like the document-free flow.
 *
 * Props:
 *   documents:    Array<{id, name}>  currently attached docs
 *   onAdd:        ({id, name}) => void
 *   onRemove:     (id) => void
 *   loading:      boolean   (parent-driven; disables CTAs during API work)
 *   onBack:       () => void
 *   onNext:       () => void
 */
const ACCEPT = '.pdf,.docx,.pptx,.txt,.md,.png,.jpg,.jpeg,.webp';
const MAX_BYTES = 15 * 1024 * 1024; // mirror FileStorageController cap

export default function AttachmentsStep({
  documents,
  onAdd,
  onRemove,
  loading,
  onBack,
  onNext,
}) {
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadError('');
    setUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        if (file.size > MAX_BYTES) {
          setUploadError(`${file.name} exceeds the 15 MB limit.`);
          continue;
        }
        const resp = await FileApi.uploadFile(file, { name: file.name });
        if (!resp.ok) {
          setUploadError(`Failed to upload ${file.name}.`);
          continue;
        }
        const body = await resp.json().catch(() => ({}));
        const id = body?.documentId ?? body?.id ?? body?.document?.id;
        if (id == null) {
          setUploadError(`Upload of ${file.name} returned no document id.`);
          continue;
        }
        onAdd({ id, name: body?.name || file.name });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [onAdd]);

  const onPickFiles = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onInputChange = useCallback((e) => {
    handleFiles(e.target.files);
  }, [handleFiles]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  return (
    <section className="goal-step">
      <h2>Anything we should know about?</h2>
      <p className="goal-step-hint">
        Attach documents that might shape your plan — a sports schedule, a syllabus,
        an activity calendar, a project brief, anything with dates or topics.
        We&apos;ll pull out fixed commitments and topics to inform the plan.
        This step is optional.
      </p>

      <div
        className="goal-attachments-dropzone"
        onClick={onPickFiles}
        onDrop={onDrop}
        onDragOver={onDragOver}
        role="button"
        tabIndex={0}
        aria-label="Add documents"
      >
        <p>
          {uploading ? 'Uploading…' : 'Click or drag files here to attach (PDF, DOCX, PPTX, images, up to 15 MB).'}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          onChange={onInputChange}
          style={{ display: 'none' }}
        />
      </div>

      {uploadError && (
        <p className="goal-attachments-error" role="alert">{uploadError}</p>
      )}

      {documents.length > 0 && (
        <ul className="goal-attachments-list">
          {documents.map((d) => (
            <li key={d.id}>
              <span>{d.name}</span>
              <button
                type="button"
                className="goal-attachments-remove"
                onClick={() => onRemove(d.id)}
                aria-label={`Remove ${d.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="goal-actions">
        <button type="button" className="goal-btn-secondary" onClick={onBack}>
          ← Back
        </button>
        <button
          type="button"
          className="goal-btn-primary"
          onClick={onNext}
          disabled={loading || uploading}
        >
          {documents.length > 0
            ? `Next: tailored questions (${documents.length} attached) →`
            : 'Skip & continue →'}
        </button>
      </div>
    </section>
  );
}
